/** Credentials and cancellation belong to one tab. Shared storage is never an auth source. */
import {SessionUnavailableError, SessionCredentialError, zitadelRolesClaim} from '@outlaw/signin';
export {SessionUnavailableError, SessionCredentialError, zitadelRolesClaim} from '@outlaw/signin';

export const APP_METADATA_CLAIM = 'http://outlawpractice.com/app_metadata';
export const USER_METADATA_CLAIM = 'http://outlawpractice.com/user_metadata';

export interface TabIdentity {
    subject: string;
    firmId: string;
    userId: string;
    portal: boolean;
}

export interface TabCredentials {
    accessToken: string;
    idToken: string;
}

export interface SessionTokenPolicy {
    issuer: string;
    clientId: string;
    /** Auth0: the API audience. Zitadel: the project ID, which also names the roles claim. */
    audience: string;
    /** Auth0 (the default) carries firm and user in app_metadata. Zitadel carries the firm as the
     * one org in the project's roles claim and no user: the server's login check supplies it. */
    provider?: 'auth0' | 'zitadel';
}

interface BoundSession extends TabCredentials {
    identity: TabIdentity;
    accessExpiresAt: number;
    idExpiresAt: number;
    reauthenticationRequired: boolean;
}

export interface SessionGeneration {
    readonly generation: number;
    readonly signal: AbortSignal;
    assertCurrent(): void;
}

export interface SessionOperation extends SessionGeneration {
    readonly identity: Readonly<TabIdentity>;
}

export interface LoginCandidate extends SessionOperation {
    readonly credentials: Readonly<TabCredentials>;
}

export class SessionChangedError extends Error {
    constructor() { super('The account session changed.'); }
}

export class SessionIdentityError extends SessionUnavailableError {
    constructor() { super('Sign in with the same account to reopen this workspace.'); }
}

function claims(token: string): Record<string, unknown> {
    try {
        const parts = token.split('.');
        if (parts.length !== 3 || parts.some(part => !part)) throw new Error();
        const value: unknown = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error();
        return value as Record<string, unknown>;
    } catch {
        throw new SessionCredentialError();
    }
}

function expiry(value: unknown): number {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || !Number.isSafeInteger(value * 1000)) {
        throw new SessionCredentialError();
    }
    return value * 1000;
}

/** What one token says about its account. A Zitadel token names no Outlaw user. */
interface TokenIdentity {
    subject: string;
    firmId: string;
    userId?: string;
    portal: boolean;
}

function auth0Identity(value: Record<string, unknown>): TokenIdentity {
    const metadata = value[APP_METADATA_CLAIM];
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) throw new SessionIdentityError();
    const {firmId, userId} = metadata as Record<string, unknown>;
    if (typeof value.sub !== 'string' || !value.sub || typeof firmId !== 'string' || !firmId ||
        typeof userId !== 'string' || !userId) throw new SessionIdentityError();
    const user = value[USER_METADATA_CLAIM];
    const portal = !!(user && typeof user === 'object' && (user as Record<string, unknown>).portalUser);
    return {subject: value.sub, firmId, userId, portal};
}

/** The one org a Zitadel token's roles claim names (design 4.3), or undefined when the claim is
 * missing or malformed, or names no org or several. */
export function zitadelFirm(value: Record<string, unknown>, projectId: string): string | undefined {
    const roles = value[zitadelRolesClaim(projectId)];
    if (!roles || typeof roles !== 'object' || Array.isArray(roles)) return undefined;
    const orgs = new Set<string>();
    for (const byOrg of Object.values(roles as Record<string, unknown>)) {
        if (!byOrg || typeof byOrg !== 'object' || Array.isArray(byOrg)) return undefined;
        for (const org of Object.keys(byOrg)) orgs.add(org);
    }
    return orgs.size === 1 ? [...orgs][0] : undefined;
}

/** The firm is the one org named in the roles claim: none or several refuse, as the server does.
 * A person holding the Practice `client` role there is a portal user. */
function zitadelIdentity(value: Record<string, unknown>, projectId: string): TokenIdentity {
    const firmId = zitadelFirm(value, projectId);
    if (typeof value.sub !== 'string' || !value.sub || !firmId) throw new SessionIdentityError();
    const roles = value[zitadelRolesClaim(projectId)] as Record<string, unknown>;
    const client = roles.client as Record<string, unknown> | undefined;
    return {subject: value.sub, firmId, portal: !!client && firmId in client};
}

function tokenIdentity(value: Record<string, unknown>, policy: SessionTokenPolicy): TokenIdentity {
    return policy.provider === 'zitadel' ? zitadelIdentity(value, policy.audience) : auth0Identity(value);
}

function sameIdentity(a: Omit<TabIdentity, 'portal'>, b: Omit<TabIdentity, 'portal'>): boolean {
    return a.subject === b.subject && a.firmId === b.firmId && a.userId === b.userId;
}

function sameTabIdentity(a: TabIdentity, b: TabIdentity): boolean {
    return sameIdentity(a, b) && a.portal === b.portal;
}

/** The SDK/server verify signatures. This checks the pair and its workspace binding.
 *
 * A Zitadel pair names no user. Its user comes from `expected`: the identity the server's login
 * check assigned (`assignUser`) or the one this tab already holds. Without one, the user is empty
 * and only `beginLogin` may hold the result. */
function bind(credentials: TabCredentials, policy: SessionTokenPolicy, expected?: TabIdentity): BoundSession {
    const id = claims(credentials.idToken);
    const access = claims(credentials.accessToken);
    const audienceMatches = (value: unknown, audience: string) => value === audience ||
        (Array.isArray(value) && value.includes(audience));
    if (id.iss !== policy.issuer || access.iss !== policy.issuer ||
        !audienceMatches(id.aud, policy.clientId) || !audienceMatches(access.aud, policy.audience)) {
        throw new SessionIdentityError();
    }
    const fromId = tokenIdentity(id, policy);
    const fromAccess = tokenIdentity(access, policy);
    if (fromId.subject !== fromAccess.subject || fromId.firmId !== fromAccess.firmId ||
        fromId.userId !== fromAccess.userId) throw new SessionIdentityError();
    // Auth0 reads the portal flag from the ID token's user_metadata only; Zitadel's roles claim is in both.
    if (policy.provider === 'zitadel' && fromId.portal !== fromAccess.portal) throw new SessionIdentityError();
    const identity: TabIdentity = {subject: fromId.subject, firmId: fromId.firmId,
        userId: fromId.userId ?? expected?.userId ?? '', portal: fromId.portal};
    if (expected && !sameTabIdentity(identity, expected)) throw new SessionIdentityError();
    return {...credentials, identity, accessExpiresAt: expiry(access.exp),
        idExpiresAt: expiry(id.exp), reauthenticationRequired: false};
}

export function isAuthenticationFailure(error: unknown): boolean {
    if (error instanceof SessionIdentityError || error instanceof SessionCredentialError) return true;
    const code = error && typeof error === 'object' && 'error' in error ? error.error : undefined;
    return ['invalid_grant', 'invalid_token', 'missing_refresh_token', 'login_required', 'consent_required',
        'interaction_required', 'mfa_required', 'access_denied'].includes(String(code));
}

/** One instance per document. Constructor injection also permits independent synthetic tabs in tests. */
export class TabSession {
    private bound?: BoundSession;
    private generation = 0;
    private controller = new AbortController();
    private renewal?: () => Promise<TabCredentials>;
    private pendingRenewal?: Promise<void>;
    private listeners = new Set<() => void>();
    private storageFailure = false;
    private revision = 0;
    private fetchedEntities?: {generation: number; names: Set<string>};
    private expiryTimer?: ReturnType<typeof setTimeout>;
    private readonly key: string;

    constructor(private readonly storage: Storage, readonly namespace: string,
        private readonly policy: SessionTokenPolicy, private readonly now = Date.now) {
        this.key = `${namespace}:session`;
        let raw: string | null;
        try { raw = storage.getItem(this.key); }
        catch { this.reportStorageFailure(); return; }
        try {
            if (raw) {
                const saved = JSON.parse(raw) as {version?: number; session?: BoundSession};
                if (saved.version !== 1 || !saved.session?.identity) throw new SessionIdentityError();
                this.bound = bind(saved.session, policy, saved.session.identity);
                this.bound.reauthenticationRequired = saved.session.reauthenticationRequired === true;
            }
        } catch {
            this.bound = undefined;
            // Do not assign malformed/foreign data to another account or import legacy credentials.
            try { this.publishIdentity(); } catch { this.reportStorageFailure(); }
            return;
        }
        // A mirror-key write failure is an unavailable store, not evidence that a validated owner is malformed.
        try { this.publishIdentity(); }
        catch { this.reportStorageFailure(); return; }
        this.scheduleExpiry();
    }

    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => { this.listeners.delete(listener); };
    };
    getRevision = (): number => this.revision;
    getIdentity = (): Readonly<TabIdentity> | undefined => this.bound ? {...this.bound.identity} : undefined;
    isLocalReady = (): boolean => !this.storageFailure && !!this.bound &&
        !this.bound.reauthenticationRequired && this.bound.idExpiresAt > this.now();
    isNetworkReady = (): boolean => this.isLocalReady() && this.bound!.accessExpiresAt > this.now();
    needsReauthentication = (): boolean => !!this.bound && !this.isLocalReady();
    hasStorageFailure = (): boolean => this.storageFailure;
    /** The session cannot continue in this tab: its store failed, or a renewal that could not
     * happen ended it. An expired ID token that is still renewable is not an ended session. */
    isSessionEnded = (): boolean => this.storageFailure || this.bound?.reauthenticationRequired === true;

    /** Validate another same-origin document's persisted binding without adopting or mutating it. */
    storageHasIdentity(storage: Pick<Storage, 'getItem'>, expected: TabIdentity): boolean {
        const raw = storage.getItem(this.key);
        if (!raw) return false;
        try {
            const saved = JSON.parse(raw) as {version?: number; session?: BoundSession};
            if (saved.version !== 1 || !saved.session?.identity) return false;
            return sameTabIdentity(bind(saved.session, this.policy, saved.session.identity).identity, expected);
        } catch (error) {
            if (error instanceof SessionUnavailableError) throw error;
            return false;
        }
    }

    /** SDK cache access may fail outside a credential request, including during startup. */
    reportStorageFailure = (): void => {
        if (this.storageFailure) return;
        this.storageFailure = true;
        if (this.bound) this.bound.reauthenticationRequired = true;
        this.invalidate();
        this.changed();
    };

    setRenewal(renewal: () => Promise<TabCredentials>): void {
        this.renewal = renewal;
        this.scheduleExpiry();
    }

    /** Fence SDK/bootstrap work even before an account has been established. */
    captureGeneration(): SessionGeneration {
        const generation = this.generation;
        const signal = this.controller.signal;
        return {generation, signal, assertCurrent: () => {
            if (generation !== this.generation || signal.aborted) throw new SessionChangedError();
        }};
    }

    /** Successful entity processing in this account generation, including later retry fetches. */
    recordFetchedEntity(operation: SessionOperation, entityName: string): void {
        operation.assertCurrent();
        if (!this.bound || !sameTabIdentity(operation.identity, this.bound.identity) ||
            operation.generation !== this.generation) throw new SessionChangedError();
        if (this.fetchedEntities?.generation !== this.generation) {
            this.fetchedEntities = {generation: this.generation, names: new Set()};
        }
        if (!this.fetchedEntities.names.has(entityName)) {
            this.fetchedEntities.names.add(entityName);
            this.changed();
        }
    }

    hasFetchedEntities = (entityNames: readonly string[]): boolean =>
        this.isLocalReady() && this.fetchedEntities?.generation === this.generation &&
        entityNames.every(name => this.fetchedEntities!.names.has(name));

    getNetworkExpiry(): number | undefined {
        return this.bound ? Math.min(this.bound.accessExpiresAt, this.bound.idExpiresAt) : undefined;
    }

    private changed(): void {
        this.revision++;
        for (const listener of this.listeners) listener();
    }

    private invalidate(preserveRenewal = false): void {
        this.generation++;
        this.controller.abort(new SessionChangedError());
        this.controller = new AbortController();
        if (!preserveRenewal) this.pendingRenewal = undefined;
        clearTimeout(this.expiryTimer);
        this.expiryTimer = undefined;
    }

    /** Renew this tab's credentials this long before its ID token expires. */
    private static readonly RENEWAL_LEAD_MS = 60_000;
    /** Wait this long before retrying a renewal that failed while the network was down. */
    private static readonly RENEWAL_RETRY_MS = 60_000;
    /** Never attempt a timer-driven renewal twice within this window, however short-lived the
     * tokens a renewal hands back are. */
    private static readonly RENEWAL_MIN_INTERVAL_MS = 60_000;
    private lastRenewalAttemptAt = 0;

    private scheduleExpiry(): void {
        clearTimeout(this.expiryTimer);
        if (!this.bound || this.storageFailure || this.bound.reauthenticationRequired) return;
        // A restored expired pair waits for its provider to be initialized, then renews immediately.
        if (!this.renewal && !this.isLocalReady()) return;
        const bound = this.bound;
        const remaining = bound.idExpiresAt - this.now();
        // Renew ahead of expiry so an idle tab is never signed out while its refresh token is
        // still good. Half the remaining life is the latest safe moment.
        const lead = this.renewal ? Math.min(TabSession.RENEWAL_LEAD_MS,
            Math.max(0, Math.floor(remaining / 2))) : 0;
        this.armExpiry(bound, remaining - lead);
    }

    private armExpiry(bound: BoundSession, delay: number): void {
        clearTimeout(this.expiryTimer);
        // Keep a provider that hands back short-lived tokens from turning the renewal timer into a
        // burst of calls: count the floor from the last attempt, so the first renewal is never
        // delayed past this token's own lead time.
        const sinceLastAttempt = this.now() - this.lastRenewalAttemptAt;
        const wait = Math.max(delay, TabSession.RENEWAL_MIN_INTERVAL_MS - sinceLastAttempt);
        this.expiryTimer = setTimeout(() => {
            if (this.bound !== bound) return;
            this.lastRenewalAttemptAt = this.now();
            // A persistence failure has already flagged the session; do not emit a stray rejection.
            void this.renewBeforeExpiry(bound).catch(() => {});
        }, Math.min(2_147_483_647, Math.max(1, wait)));
    }

    /** Renew before the ID token expires. The refresh token, not the ID token, authenticates this
     * call, so an already expired ID token is still renewable. A renewal that fails while the
     * network is down keeps the session and is retried; only a renewal that cannot happen at all
     * ends the session, which also keeps its owner eligible for a later online renewal. */
    private async renewBeforeExpiry(bound: BoundSession): Promise<void> {
        if (!this.renewal) {
            // Nothing in this tab can renew the session, so it is over.
            this.requireReauthentication();
            return;
        }
        try {
            await this.credentials(this.operation(bound.identity), true);
            return;
        } catch {
            // An authentication failure already ended the session; a replaced binding or a failed
            // store leaves nothing here to retry for.
            if (this.bound !== bound || this.storageFailure || bound.reauthenticationRequired) return;
        }
        // Transient: keep the session and try again. Local readiness stays false until a renewal
        // succeeds, and the tab is never locked.
        this.armExpiry(bound, TabSession.RENEWAL_RETRY_MS);
    }

    private publishIdentity(): void {
        for (const [key, value] of [['firmId', this.bound?.identity.firmId], ['userId', this.bound?.identity.userId]]) {
            // These keys name this bound account's local stores. They are not
            // request authority, so retain them while the workspace is locked
            // and clear them only when the tab deliberately signs out.
            if (value) this.storage.setItem(key, value);
            else this.storage.removeItem(key);
        }
    }

    private save(): void {
        try {
            if (this.bound) this.storage.setItem(this.key, JSON.stringify({version: 1, session: this.bound}));
            else this.storage.removeItem(this.key);
            this.publishIdentity();
            this.scheduleExpiry();
        } catch {
            this.reportStorageFailure();
            throw new SessionUnavailableError('Tab storage is unavailable. Enable it before signing in.');
        }
        this.changed();
    }

    private operation(identity: TabIdentity, requiresLocalReadiness = false): SessionOperation {
        const generation = this.generation;
        const signal = this.controller.signal;
        const localDeadline = requiresLocalReadiness ? this.bound?.idExpiresAt : undefined;
        return {identity: {...identity}, generation, signal, assertCurrent: () => {
            if (generation !== this.generation || signal.aborted) throw new SessionChangedError();
            // Background-tab timers may be throttled; late local work also checks actual expiry.
            if (requiresLocalReadiness && (!this.isLocalReady() || localDeadline! <= this.now())) {
                throw new SessionUnavailableError();
            }
        }};
    }

    capture(): SessionOperation {
        if (!this.isLocalReady()) throw new SessionUnavailableError();
        return this.operation(this.bound!.identity, true);
    }

    /** An expired ID token may be renewed online, but cannot unlock local data first. */
    captureRequest(): SessionOperation {
        if (this.storageFailure || !this.bound || this.bound.reauthenticationRequired) throw new SessionUnavailableError();
        return this.operation(this.bound.identity);
    }

    beginLogin(credentials: TabCredentials): LoginCandidate {
        if (this.storageFailure) throw new SessionUnavailableError('Tab storage is unavailable.');
        // The SDK may already have returned a different account. Suspend the old workspace
        // before validating that result, retaining its owner for matching reauthentication.
        if (this.bound) this.requireReauthentication();
        else this.invalidate();
        const candidate = bind(credentials, this.policy, this.bound?.identity);
        if (Math.min(candidate.idExpiresAt, candidate.accessExpiresAt) <= this.now()) throw new SessionUnavailableError();
        return {...this.operation(candidate.identity), credentials: {...credentials}};
    }

    /** The user the server's login check answered for a candidate whose tokens name none
     * (Zitadel). A candidate that already has a user keeps it; a different one refuses. */
    assignUser(candidate: LoginCandidate, userId: string): LoginCandidate {
        candidate.assertCurrent();
        if (!userId || (candidate.identity.userId && candidate.identity.userId !== userId)) {
            throw new SessionIdentityError();
        }
        return {...candidate, identity: {...candidate.identity, userId}};
    }

    /** Call only after the candidate's online login checks succeed. */
    completeLogin(candidate: LoginCandidate): void {
        candidate.assertCurrent();
        if (!candidate.identity.userId) throw new SessionIdentityError();
        const next = bind(candidate.credentials, this.policy, candidate.identity);
        if (Math.min(next.idExpiresAt, next.accessExpiresAt) <= this.now()) throw new SessionUnavailableError();
        this.bound = next;
        this.save();
    }

    async credentials(operation = this.captureRequest(), forceRenewal = false): Promise<TabCredentials> {
        operation.assertCurrent();
        if (!this.bound || !sameIdentity(operation.identity, this.bound.identity) || this.bound.reauthenticationRequired) {
            throw new SessionUnavailableError();
        }
        if (forceRenewal || Math.min(this.bound.idExpiresAt, this.bound.accessExpiresAt) <= this.now() + 30_000) {
            if (!this.renewal) throw new SessionUnavailableError('Reconnect and sign in to continue.');
            if (!this.pendingRenewal) {
                const renewing = this.bound;
                const expected = {...renewing.identity};
                const assertOwner = () => {
                    // Expiry may cancel the caller while this same owner's renewal is in flight.
                    // Its result still updates/locks that owner; logout or a new login never does.
                    if (this.bound !== renewing || renewing.reauthenticationRequired) throw new SessionChangedError();
                };
                const pending = this.renewal().then(value => {
                    assertOwner();
                    const next = bind(value, this.policy, expected);
                    if (Math.min(next.idExpiresAt, next.accessExpiresAt) <= this.now()) throw new SessionCredentialError();
                    this.bound = next;
                    this.save();
                }).catch(error => {
                    assertOwner();
                    if (isAuthenticationFailure(error)) this.requireReauthentication();
                    throw error;
                }).finally(() => {
                    if (this.pendingRenewal === pending) this.pendingRenewal = undefined;
                });
                this.pendingRenewal = pending;
            }
            await this.pendingRenewal;
        }
        operation.assertCurrent();
        if (!this.isNetworkReady()) throw new SessionUnavailableError();
        return {accessToken: this.bound!.accessToken, idToken: this.bound!.idToken};
    }

    /** No renewal and no shared-token fallback; useful only for synchronous socket handshakes. */
    peekCredentials(): TabCredentials | undefined {
        return this.isNetworkReady() ? {accessToken: this.bound!.accessToken, idToken: this.bound!.idToken} : undefined;
    }

    requireReauthentication(): void {
        if (!this.bound) return;
        this.bound.reauthenticationRequired = true;
        this.invalidate();
        this.save();
    }

    /** Does not delete account databases, queues, preferences, or another tab's credentials. */
    signOut(): void {
        this.invalidate();
        this.bound = undefined;
        this.save();
    }
}

/** Auth0's cache keys remain opaque; enumeration/removal are confined to this app/client namespace. */
export function createSessionCache(storage: Storage, namespace: string, onStorageFailure?: () => void) {
    const prefix = `${namespace}:sdk:`;
    const accessStorage = <T>(operation: () => T): T => {
        try { return operation(); }
        catch {
            onStorageFailure?.();
            throw new SessionUnavailableError('Tab storage is unavailable. Enable it before signing in.');
        }
    };
    return {
        get<T>(key: string): T | undefined {
            const raw = accessStorage(() => storage.getItem(prefix + key));
            if (raw === null) return undefined;
            try { return JSON.parse(raw) as T; }
            catch { return undefined; }
        },
        set<T>(key: string, value: T): void {
            const serialized = JSON.stringify(value);
            accessStorage(() => storage.setItem(prefix + key, serialized));
        },
        remove(key: string): void { accessStorage(() => storage.removeItem(prefix + key)); },
        allKeys(): string[] {
            return accessStorage(() => {
                const keys: string[] = [];
                for (let index = 0; index < storage.length; index++) {
                    const key = storage.key(index);
                    if (key?.startsWith(prefix)) keys.push(key.slice(prefix.length));
                }
                return keys;
            });
        },
    };
}
